import React from 'react';

/**
 * A marca FROVOX.
 *
 * Existe como componente único porque a logo aparece em cinco telas e, espalhada
 * em `<img>` soltos, uma troca de arquivo exigiria caçar todas — e a que
 * escapasse ficaria com a marca antiga sem ninguém notar.
 *
 * São duas versões, e a escolha não é estética:
 *
 * - **completa** — o círculo com o nome e a assinatura dentro. Só serve grande
 *   (a partir de ~80px); abaixo disso o texto vira borrão.
 * - **simbolo** — só o F com a estrada. É o que vai nos lugares pequenos, onde
 *   o nome aparece como texto ao lado, e não dentro da imagem.
 *
 * Repetir o nome ao lado da versão completa duplicaria a palavra na tela, então
 * `mostrarNome` só faz sentido com a versão símbolo.
 */

type Versao = 'completa' | 'simbolo';

interface MarcaProps {
  versao?: Versao;
  /** Lado da imagem em pixels. */
  tamanho?: number;
  /** Escreve "FROVOX" e a assinatura ao lado. Só com a versão símbolo. */
  mostrarNome?: boolean;
  className?: string;
}

const ARQUIVO: Record<Versao, string> = {
  completa: '/marca/frovox.png',
  simbolo: '/marca/frovox-simbolo.png',
};

const Marca: React.FC<MarcaProps> = ({
  versao = 'completa',
  tamanho = 64,
  mostrarNome = false,
  className = '',
}) => {
  const imagem = (
    <img
      src={ARQUIVO[versao]}
      // O alt descreve a marca, e não o arquivo: é o que um leitor de tela
      // anuncia quando a imagem não carrega.
      alt="FROVOX — Gestão Inteligente de Frotas"
      width={tamanho}
      height={tamanho}
      // A imagem já é recortada em círculo, mas o arredondamento no CSS garante
      // o formato mesmo que alguém troque o arquivo por um quadrado.
      className={versao === 'completa' ? 'rounded-full' : ''}
      style={{ width: tamanho, height: tamanho, objectFit: 'contain' }}
    />
  );

  if (!mostrarNome) {
    return <span className={className}>{imagem}</span>;
  }

  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      {imagem}
      <span className="leading-tight">
        <span className="block text-lg font-semibold text-secondary-900">FROVOX</span>
        <span className="block text-xs text-secondary-500">Gestão Inteligente de Frotas</span>
      </span>
    </span>
  );
};

export default Marca;
